import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import CheckIcon from "@assets/icons/check.svg?raw&inline";
import LoadingDots from "./LoadingDots";
import SearchInput from "../../components/SearchInput";
import TextInput from "../../components/TextInput";
import SelectInput from "../../components/SelectInput";
import RadioGroup from "../../components/RadioGroup";
import {
  OBJETO_SOCIAL,
  CAPITAL_SOCIAL_MONTO,
  SI_NO_OPTIONS,
  type IncorporationWizardInitialData,
} from "@/schemas/incorporation";
import { apiRequest } from "@/utils";
import { COMPANY_NAME_CHECK_API } from "@/constant";
import {
  createOrUpdateIncorporationRequest,
  IncorporationRequestError,
} from "@/services/incorporationRequest";
import { CustomTooltip } from "../../CustomTooltip";

type CompanyNameStatus = "idle" | "loading" | "available" | "unavailable";

// Zod schema for SECCION I - Nombre de la empresa
const nombreEmpresaSchema = z.object({
  nombre: z
    .string()
    .min(1, "company_name.validation.required")
    .refine((name) => /(CORP|INC\.|S\.A\.)$/i.test(name.trim()), {
      message: "company_name.validation.invalid_suffix",
    }),
});

type CompanyNameSearchData = z.infer<typeof nombreEmpresaSchema>;

const [OBJETO_SOCIAL_ESTANDAR, OBJETO_SOCIAL_OTRO] = OBJETO_SOCIAL;
const [CAPITAL_SOCIAL_MONTO_ESTANDAR, CAPITAL_SOCIAL_MONTO_OTRO] =
  CAPITAL_SOCIAL_MONTO;

const capitalSocialSchema = z
  .object({
    capital_social_monto: z.enum(CAPITAL_SOCIAL_MONTO, {
      message: "company_name.capital_social.validation.amount_required",
    }),
    capital_social_monto_otro: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.capital_social_monto === CAPITAL_SOCIAL_MONTO_OTRO) {
        return (
          data.capital_social_monto_otro !== undefined &&
          data.capital_social_monto_otro.trim().length > 0
        );
      }
      return true;
    },
    {
      message: "company_name.capital_social.validation.amount_other_required",
      path: ["capital_social_monto_otro"],
    }
  );

// objeto_social_otro is required only when objeto_social === "Otro"
const companyDetailsSchema = z
  .discriminatedUnion("objeto_social", [
    // Variant 1: Estandar (no objeto_social_otro needed)
    z.object({
      objeto_social: z.literal(OBJETO_SOCIAL_ESTANDAR),
      actividad_principal: z
        .string()
        .trim()
        .min(1, "company_name.main_activity.required"),
      opera_panama: z.enum(SI_NO_OPTIONS, {
        message: "company_name.operates_in_panama.required",
      }),
    }),
    // Variant 2: Otro (objeto_social_otro is required)
    z.object({
      objeto_social: z.literal(OBJETO_SOCIAL_OTRO),
      objeto_social_otro: z
        .string()
        .trim()
        .min(1, "company_name.social_object.other.required"),
      actividad_principal: z
        .string()
        .trim()
        .min(1, "company_name.main_activity.required"),
      opera_panama: z.enum(SI_NO_OPTIONS, {
        message: "company_name.operates_in_panama.required",
      }),
    }),
  ])
  .and(capitalSocialSchema);

type CompanyDetailsFormData = z.infer<typeof companyDetailsSchema>;

/**
 * Hardcoded fetch function - returns boolean indicating if name is available
 * TODO: Replace with actual API endpoint when ready
 * Note: Suffix validation is handled by Zod schema, so this function only checks availability
 * @param name - The company name to check (already validated to have correct suffix)
 * @returns true if name is available, false if not
 * @throws Error if name is not available
 */
const fetchNameAvailability = async (name: string): Promise<boolean> => {
  const response = await apiRequest(
    `${COMPANY_NAME_CHECK_API}?nombre=${encodeURIComponent(name)}`
  );
  if (response.error) {
    throw new Error(response.error);
  }
  return response.available;
};

// CompanyNameSearch Component
interface CompanyNameSearchProps {
  onNameAvailable?: (name: string) => void;
}

function CompanyNameSearch({ onNameAvailable }: CompanyNameSearchProps) {
  const { t } = useTranslation();
  const { setData, data } = useIncorporationWizardFormCtx();
  const [status, setStatus] = useState<CompanyNameStatus>("idle");
  const [searchedName, setSearchedName] = useState("");

  const nombre = data?.incorporationRequest?.nombre;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
  } = useForm<CompanyNameSearchData>({
    resolver: zodResolver(nombreEmpresaSchema),
    defaultValues: {
      nombre: nombre || "",
    },
  });

  const checkNameAvailability = async (name: string) => {
    setSearchedName(name);
    setStatus("loading");
    try {
      const isAvailable = await fetchNameAvailability(name);
      if (isAvailable) {
        setStatus("available");
        setData((prevData) => {
          return {
            ...prevData,
            incorporationRequest: {
              ...(prevData?.incorporationRequest || {}),
              nombre: name,
            },
            incorporationContact: prevData?.incorporationContact || [],
            currentUser: prevData?.currentUser,
            incorporationStripePrice: prevData?.incorporationStripePrice,
            path: prevData?.path || "",
          };
        });
        onNameAvailable?.(name);
      } else {
        setStatus("unavailable");
      }
    } catch (error) {
      setError("nombre", {
        message:
          error instanceof Error
            ? error.message
            : "Error checking name availability",
      });
      setStatus("idle");
    }
  };

  const onSubmit = async (formData: CompanyNameSearchData) => {
    // Zod validation already passed at this point
    const name = formData.nombre.trim();
    await checkNameAvailability(name);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex flex-column gap-3">
        <SearchInput
          name="nombre"
          control={control}
          placeholder={t("company_name.input_placeholder")}
          disabled={status === "loading" || isSubmitting}
        />

        {/* Loading State */}
        {status === "loading" && (
          <LoadingDots text={t("company_name.verifying")} />
        )}

        {/* Unavailable State */}
        {status === "unavailable" && (
          <p className="text-danger mb-0">
            {searchedName} {t("company_name.unavailable_message")}
          </p>
        )}

        {/* Available State */}
        {status === "available" && nombre && (
          <div className="d-flex align-items-center justify-content-between gap-2 border border-success rounded p-2 px-4">
            <span className="fw-bold">{nombre}</span>
            <div className="d-flex align-items-center gap-1 text-success">
              <span dangerouslySetInnerHTML={{ __html: CheckIcon }} />
              {t("company_name.available")}
            </div>
          </div>
        )}
      </div>
    </form>
  );
}

// CompanyDetailsForm Component
function CompanyDetailsForm() {
  const { t } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();
  const [showFullSocialObject, setShowFullSocialObject] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setError: setFormError,
    formState: { isSubmitting },
  } = useForm<CompanyDetailsFormData>({
    resolver: zodResolver(companyDetailsSchema),
    defaultValues: {
      objeto_social:
        data?.incorporationRequest?.objeto_social || OBJETO_SOCIAL_ESTANDAR,
      objeto_social_otro: data?.incorporationRequest?.objeto_social_otro || "",
      actividad_principal:
        data?.incorporationRequest?.actividad_principal || "",
      opera_panama: data?.incorporationRequest?.opera_panama,
      capital_social_monto: data?.incorporationRequest?.capital_social_monto,
      capital_social_monto_otro:
        data?.incorporationRequest?.capital_social_monto_otro || "",
    },
  });

  const selectedSocialObject = watch("objeto_social");
  const selectedCapitalSocialMonto = watch("capital_social_monto");
  const socialObjectFullText = t(
    "company_name.social_object.standard.full_text"
  );
  const socialObjectLessText = t(
    "company_name.social_object.standard.less_text"
  );

  const onSubmit = async (formData: CompanyDetailsFormData) => {
    setSaveError(null);

    try {
      // Prepare data to send - include existing id if present, and merge with form data
      const requestData = {
        ...data?.incorporationRequest,
        ...formData,
      };

      // Call API to create or update
      const response = await createOrUpdateIncorporationRequest(requestData);

      // Update local state with response data (especially important for new requests to get the id)
      setData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          incorporationRequest: {
            ...prevData.incorporationRequest,
            ...response.data,
            id: response.id, // Ensure id is set
          },
          incorporationContact: prevData.incorporationContact || [],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });

      // Navigate to next step on success
      next();
    } catch (error) {
      // Handle different error types
      if (error instanceof IncorporationRequestError) {
        const errorMessage =
          error.errors && error.errors.length > 0
            ? error.errors.join(", ")
            : error.message;
        setSaveError(errorMessage);

        // For validation errors (422), try to set field-specific errors if possible
        if (error.statusCode === 422 && error.errors) {
          // Set a general form error - could be enhanced to map to specific fields
          setFormError("root", { message: errorMessage });
        }
      } else {
        const errorMessage =
          error instanceof Error ? error.message : "Error al guardar los datos";
        setSaveError(errorMessage);
        setFormError("root", { message: errorMessage });
      }
      // Re-throw to prevent navigation on error
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex flex-column gap-5 mt-4">
        {/* Social Object Section */}
        <div className="text-start">
          <h2 className="fw-bold mb-3 size-18">
            {t("company_name.social_object.title")}
          </h2>

          <div className="d-flex flex-column gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="socialObjectStandard"
                value={OBJETO_SOCIAL_ESTANDAR}
                {...register("objeto_social")}
              />
              <label
                className="form-check-label size-14"
                htmlFor="socialObjectStandard"
              >
                <div>
                  <span className="fw-bold">
                    {t("company_name.social_object.standard.label")}:{" "}
                  </span>
                  {showFullSocialObject ? (
                    <span>
                      <span>{socialObjectLessText}</span>
                      <span className="d-block mt-3">
                        {socialObjectFullText}
                      </span>
                      <button
                        type="button"
                        className="btn btn-link p-0 size-14 text-decoration-none"
                        onClick={() => setShowFullSocialObject(false)}
                      >
                        {t("company_name.social_object.read_less")}
                      </button>
                    </span>
                  ) : (
                    <span>
                      {socialObjectLessText}&nbsp;
                      <button
                        type="button"
                        className="btn btn-link p-0 size-14 text-decoration-none"
                        onClick={() => setShowFullSocialObject(true)}
                      >
                        {t("company_name.social_object.read_more")}
                      </button>
                    </span>
                  )}
                </div>
              </label>
            </div>

            <div className="form-check size-14">
              <input
                className="form-check-input"
                type="radio"
                id="socialObjectOther"
                value={OBJETO_SOCIAL_OTRO}
                {...register("objeto_social")}
              />
              <label className="form-check-label" htmlFor="socialObjectOther">
                {t("company_name.social_object.other.label")}
              </label>
            </div>

            {selectedSocialObject === OBJETO_SOCIAL_OTRO && (
              <TextInput
                name="objeto_social_otro"
                control={control}
                placeholder={t("company_name.social_object.other.placeholder")}
              />
            )}
          </div>
        </div>

        {/* Main Activity Section */}
        <div>
          <h2 className="fw-bold size-18">
            {t("company_name.main_activity.title")}
          </h2>
          <p className="text-muted size-14">
            {t("company_name.main_activity.example")}
          </p>
          <p className="text-muted mb-2">
            {t("company_name.main_activity.subtitle")}
          </p>
          <TextInput
            name="actividad_principal"
            control={control}
            placeholder={t("company_name.main_activity.placeholder")}
          />
        </div>

        {/* Capital Social Section */}
        <div className="d-flex flex-column gap-3">
          <h2 className="fw-bold mb-3 size-18 d-flex align-items-center gap-1">
            {t("company_name.capital_social.title")}
            <CustomTooltip
              text={t("company_name.capital_social.tooltip_content")}
            />
          </h2>

          <SelectInput
            name="capital_social_monto"
            control={control}
            label={t("company_name.capital_social.amount.label")}
            placeholder={t("company_name.capital_social.amount.placeholder")}
            options={[
              {
                value: CAPITAL_SOCIAL_MONTO_ESTANDAR,
                label: t(
                  "company_name.capital_social.amount.standard_option"
                ),
              },
              {
                value: CAPITAL_SOCIAL_MONTO_OTRO,
                label: t("company_name.capital_social.amount.other_option"),
              },
            ]}
            isMulti={false}
          />

          {selectedCapitalSocialMonto === CAPITAL_SOCIAL_MONTO_OTRO && (
            <TextInput
              name="capital_social_monto_otro"
              control={control}
              label={t("company_name.capital_social.amount.other_label")}
              placeholder={t(
                "company_name.capital_social.amount.other_placeholder"
              )}
            />
          )}
        </div>

        {/* Operates in Panama Section */}
        <div>
          <h2 className="fw-bold mb-3 size-18 d-flex align-items-center gap-1">
            {t("company_name.operates_in_panama.title")}
          <CustomTooltip
            text={t("company_name.operates_in_panama.tooltip_content")}
          />
          </h2>
          <RadioGroup
            name="opera_panama"
            control={control}
            options={[
              {
                value: SI_NO_OPTIONS[0],
                label: t("company_name.operates_in_panama.yes"),
              },
              {
                value: SI_NO_OPTIONS[1],
                label: t("company_name.operates_in_panama.no"),
              },
            ]}
          />
        </div>

        {/* Error Message */}
        {saveError && (
          <div className="alert alert-danger" role="alert">
            {saveError}
          </div>
        )}

        {/* Continue Button */}
        <div className="d-flex justify-content-center">
          <NextButton
            type="submit"
            label={t("company_name.continue")}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </form>
  );
}

// Main CompanyNameStep Component
const CompanyNameStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data } = useIncorporationWizardFormCtx();
  const nombre = data?.incorporationRequest?.nombre;

  return (
    <Layout
      title={t("company_name.title")}
      description={
        <span>
          {t("company_name.description")}
          <CustomTooltip
            text={t("company_name.description_review_status")}
          />
        </span>
      }
    >
      <div className="d-flex flex-column gap-4 mt-2">
        <CompanyNameSearch />

        {nombre && <CompanyDetailsForm key={nombre} />}
      </div>
    </Layout>
  );
};

CompanyNameStep.title = (t) => t("company_name.title");
CompanyNameStep.description = (t) =>
  t("company_name.description_progress_panel");
CompanyNameStep.isComplete = (data: IncorporationWizardInitialData) => {
  const details = companyDetailsSchema.safeParse(data.incorporationRequest);
  return details.success && Boolean(data.incorporationRequest.nombre);
};

export default CompanyNameStep;
